// FLAGS: identity, personal-data, health-data, legacy
// Every Redux thunk that changes who the browser thinks is signed in: password login, the guest and SSO
// paths, 2FA completion, registration, profile update, token refresh and logout. Each calls
// services/auth.service.ts and then dispatches into authReducers.ts, which is what sets `isLoggedIn`.
import { AxiosError } from "axios";
import { Dispatch } from "redux";
import AuthService, {
  getUserProfile,
  RegisterGuestUserPayload,
  RegisterUserPayload,
  UpdateUserPayload,
} from "@src/services/auth.service";
import { pushPreferencesFromStorageToServer } from "@src/services/preferences.service";
import { pushCurrentMoodsFromStorageToServer } from "@src/services/currentMoods.service";
import { saveUnconfirmedCheckin } from "@src/services/tripetto.service";
import sessionStorageService from "@src/services/sessionStorage.service";
import localStorageService from "@src/services/localStorage.service";
import { sendLoginEvent, LoginMethod } from "@src/services/event.service";
import { clearPhaseTwoSession } from "@src/services/phaseTwoSession";
import { RootState } from "@src/reducers/reduxState";
import { refreshUserSessionId } from "@src/utils/userSessionId";

import { trackException } from "@src/appInsights/AppInsights";
import { AuthInsightEvent } from "@src/appInsights/appInsights.types";
import { ResponseExternalLoginRegisterCodeFlowResponse } from "@src/api/v2/data-contracts";
import {
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  LOGIN_SUCCESS,
  TWO_FACTOR_PENDING,
  TWO_FACTOR_SUCCESS,
  LOGIN_FAIL,
  LOGOUT,
  RESEND_CODE_SUCCESS,
  RESEND_CODE_FAIL,
  VERIFY_SUCCESS,
  VERIFY_FAIL,
  FORGOT_PASSWORD_FAIL,
  FORGOT_PASSWORD_SUCCESS,
  CLEAR_MESSAGE,
  REGISTER_GUEST_SUCCESS,
  UPDATE_SUCCESS,
  UPDATE_FAIL,
  REGISTER_EXTERNAL_SUCCESS,
} from "./typeActions";

// Which cookies the sign-in just produced. The Phase Two marker now outlives the tab, so a legacy
// sign-in in a browser that once signed in through Keycloak has to clear it, or logout and token
// refresh would keep taking the Phase Two path against legacy cookies. Legacy is the default on
// purpose: a new sign-in path that forgets to say which kind it is clears the marker rather than
// inheriting a stale one.
type SessionKind = "legacy" | "phaseTwo";

const dispatchLoginSuccessWithEvent = (
  dispatch: Dispatch,
  data: { id: string | null | undefined } | null,
  loginMethod: LoginMethod,
  sessionKind: SessionKind = "legacy",
) => {
  if (sessionKind === "legacy") {
    clearPhaseTwoSession();
  }
  dispatch({
    type: LOGIN_SUCCESS,
    payload: { user: data },
  });
  if (data?.id) {
    sendLoginEvent(loginMethod);
  }
};

// FLAGS: health-data
// Flushes everything the visitor answered before they had an account — Tripetto check-in answers, theme
// preferences, current moods — out of browser storage and onto the server, addressed by email plus the
// `unconfirmedDataToken` the registration call returned. Three awaited calls with no catch of their own, so
// a rejection in the first abandons the other two; `registerGuest` turns that into a trackException and
// dispatches nothing, leaving the answers stranded in storage with no error on screen.
const processPostRegistrationData = async (
  email: string,
  unconfirmedDataToken?: string,
) => {
  const tripettoAnswers = sessionStorageService.getCheckinLocal();
  if (tripettoAnswers?.formModules) {
    await saveUnconfirmedCheckin(
      email,
      unconfirmedDataToken,
      tripettoAnswers.formModules,
      tripettoAnswers.formLanguage,
      tripettoAnswers.answers,
    );
  }
  await pushPreferencesFromStorageToServer(email, unconfirmedDataToken);
  await pushCurrentMoodsFromStorageToServer(email, unconfirmedDataToken);
};

export const registerGuest =
  (payload: RegisterGuestUserPayload) => async (dispatch: Dispatch) => {
    const { email } = payload;
    try {
      const unconfirmedDataToken = await AuthService.registerGuestUser(payload);
      localStorageService.setUnconfirmedDataSignature(unconfirmedDataToken);
      await processPostRegistrationData(email, unconfirmedDataToken);
      dispatch({
        type: REGISTER_GUEST_SUCCESS,
        payload: {
          user: {
            email,
          },
        },
      });
    } catch {
      trackException(AuthInsightEvent.RegisterGuestFailed);
    }
  };

export const register =
  (payload: RegisterUserPayload) => async (dispatch: Dispatch) => {
    const { email } = payload;
    try {
      const unconfirmedDataToken = await AuthService.registerUser(payload);

      localStorageService.setUnconfirmedDataSignature(unconfirmedDataToken);

      await processPostRegistrationData(
        email,
        unconfirmedDataToken?.unconfirmedDataId,
      );

      dispatch({
        type: REGISTER_SUCCESS,
        payload: {
          user: {
            email,
          },
        },
      });
    } catch (error) {
      trackException(AuthInsightEvent.RegistrationFailed);
      throw error;
    }
  };

// FLAGS: identity
// The failure branch dispatches UPDATE_FAIL with `user: null`, and the reducer hands that straight to
// `saveUserToStorage`, which treats null as "clear" — so a rejected profile update wipes the stored user and
// flips `isLoggedIn` to false. A failed save signs the tab out.
export const update =
  (payload: UpdateUserPayload) => async (dispatch: Dispatch) => {
    try {
      const response = await AuthService.updateUser(payload);
      await processPostRegistrationData(payload.email);
      dispatch({
        type: UPDATE_SUCCESS,
        payload: { user: response },
      });
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      dispatch({
        type: UPDATE_FAIL,
        payload: {
          user: null,
          error: axiosError.request?.responseText || axiosError.message,
        },
      });
      throw error;
    }
  };

export const setFocusThemes =
  (focusedThemes: string[]) => async (dispatch: Dispatch) => {
    try {
      const response = AuthService.setFocusThemes(focusedThemes);
      dispatch({
        type: UPDATE_SUCCESS,
        payload: { user: response },
      });
      return await response;
    } catch (error) {
      const axiosError = error as AxiosError;
      dispatch({
        type: UPDATE_FAIL,
        payload: {
          user: null,
          error: axiosError.request?.responseText || axiosError.message,
        },
      });
      throw error;
    }
  };

export const verifyEmail =
  (code: string, email: string) => async (dispatch: Dispatch) => {
    const codeWithPlusInsteadOfSpace = code?.replaceAll(" ", "+");
    try {
      const response = await AuthService.verifyUserAccount(
        codeWithPlusInsteadOfSpace,
        email,
      );
      /* This is the case when the user is
    verifying their account and the signature is not
    available in the local storage (in-cognito mode).
    In such cases handle this gracefully
    and make the user go to login page */
      if (response === undefined) {
        return response;
      }

      // A verified legacy registration is a legacy sign-in; see dispatchLoginSuccessWithEvent.
      clearPhaseTwoSession();
      dispatch({
        type: VERIFY_SUCCESS,
        payload: {
          user: response,
        },
      });
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.VerifyEmailFailed);
      dispatch({
        type: VERIFY_FAIL,
        payload: {
          user: axiosError.response?.data ? axiosError.response.data : null,
          error: axiosError.request?.responseText || axiosError.message,
        },
      });
      throw error;
    }
  };

export const resendCodeEmail =
  (email: string, redirectUri?: string) => async (dispatch: Dispatch) => {
    try {
      const response = await AuthService.resendVerificationEmail(
        email,
        redirectUri,
      );

      dispatch({
        type: RESEND_CODE_SUCCESS,
        payload: {
          successMessage: response,
        },
      });
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.ResendCodeEmailFailed);
      dispatch({
        type: RESEND_CODE_FAIL,
        payload: {
          error: axiosError.request?.responseText || axiosError.message,
        },
      });
      throw error;
    }
  };

const isTwoFactorPending = (
  data: unknown,
): data is ResponseExternalLoginRegisterCodeFlowResponse =>
  typeof data === "object" &&
  data !== null &&
  "twoFactorVerification" in data &&
  (data as ResponseExternalLoginRegisterCodeFlowResponse)
    .twoFactorVerification === "pending";

export const getGuestUser =
  (token: string, email: string) => async (dispatch: Dispatch) => {
    try {
      const data = await AuthService.getGuestUser(token, email);
      if (isTwoFactorPending(data)) {
        dispatch({
          type: TWO_FACTOR_PENDING,
          payload: {
            user: data,
          },
        });
      } else {
        dispatchLoginSuccessWithEvent(dispatch, data, "guest");
      }
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.GetGuestUserFailed);
      dispatch({
        type: LOGIN_FAIL,
        error: axiosError.request?.responseText || axiosError.message,
      });
      throw error;
    }
  };

export const getExternalUser =
  (idToken: string, provider: string) => async (dispatch: Dispatch) => {
    try {
      const data = await AuthService.getExternalUser(idToken, provider);
      if (isTwoFactorPending(data)) {
        dispatch({
          type: TWO_FACTOR_PENDING,
          payload: {
            user: data,
          },
        });
      } else {
        dispatchLoginSuccessWithEvent(dispatch, data, "external");
      }
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.GetExternalUserFailed);
      dispatch({
        type: LOGIN_FAIL,
        error: axiosError.request?.responseText || axiosError.message,
      });
      throw error;
    }
  };

export const getExternalUserCodeFlow =
  (code: string, provider: string) => async (dispatch: Dispatch) => {
    try {
      const data = await AuthService.getExternalUserCodeFlow(code, provider);
      if (data?.twoFactorVerification === "pending") {
        dispatch({
          type: TWO_FACTOR_PENDING,
          payload: {
            user: data,
          },
        });
      } else {
        dispatchLoginSuccessWithEvent(dispatch, data, "external");
      }
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.GetExternalUserCodeFlowFailed);
      dispatch({
        type: LOGIN_FAIL,
        error: axiosError.request?.responseText || axiosError.message,
      });
      throw error;
    }
  };

/**
 * Establish the app session after a Phase Two sign-in (OI-25561). The API is
 * already holding the realm tokens and this browser is holding its session
 * cookie, so the profile fetch carries it and the API resolves (or provisions)
 * the OpenUp user from the token behind it — there is no external-login
 * exchange in this mode. 2FA never comes back "pending" here: Keycloak enforced
 * it during login.
 */
export const phaseTwoLogin = () => async (dispatch: Dispatch) => {
  try {
    refreshUserSessionId();
    const data = await getUserProfile();
    if (!data) {
      throw new Error("Phase Two sign-in could not load the profile");
    }
    dispatchLoginSuccessWithEvent(dispatch, data, "external", "phaseTwo");
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    trackException(AuthInsightEvent.GetExternalUserFailed);
    dispatch({
      type: LOGIN_FAIL,
      error: axiosError.request?.responseText || axiosError.message,
    });
    throw error;
  }
};

export const signUpExternalUserCodeFlow =
  (code: string, provider: string) => async (dispatch: Dispatch) => {
    try {
      const data = await AuthService.signUpExternalUserCodeFlow(code, provider);
      if (
        data.twoFactorVerification &&
        data.twoFactorVerification === "pending"
      ) {
        dispatch({
          type: TWO_FACTOR_PENDING,
          payload: {
            user: data,
          },
        });
      } else {
        // A legacy social sign-up signs the browser in the legacy way; see dispatchLoginSuccessWithEvent.
        clearPhaseTwoSession();
        dispatch({
          type: REGISTER_EXTERNAL_SUCCESS,
          payload: { user: data },
        });
      }
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.SignUpExternalUserCodeFailed);
      dispatch({
        type: REGISTER_FAIL,
        payload: {
          error:
            axiosError.response?.data ||
            axiosError.request?.responseText ||
            axiosError.message,
        },
      });
      throw error;
    }
  };

export const signUpExternalUser =
  (idToken: string, provider: string) => async (dispatch: Dispatch) => {
    try {
      const data = await AuthService.signUpExternalUser(idToken, provider);

      if (data?.twoFactorVerification === "pending") {
        dispatch({
          type: TWO_FACTOR_PENDING,
          payload: {
            user: data,
          },
        });
      } else {
        // A legacy social sign-up signs the browser in the legacy way; see dispatchLoginSuccessWithEvent.
        clearPhaseTwoSession();
        dispatch({
          type: REGISTER_EXTERNAL_SUCCESS,
          payload: { user: data },
        });
      }
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.SignUpExternalUserFailed);
      dispatch({
        type: REGISTER_FAIL,
        payload: {
          error:
            axiosError.response?.data ||
            axiosError.request?.responseText ||
            axiosError.message,
        },
      });
      throw error;
    }
  };

// FLAGS: identity
// Resolves the same way whether the refresh worked or not — it returns nothing and rethrows nothing, and
// on failure dispatches LOGIN_FAIL, whose reducer case sets `isLoggedIn: false` without clearing storage.
// Callers that only `await` this cannot tell the two outcomes apart.
export const refreshAccessToken =
  (token: string) => async (dispatch: Dispatch) => {
    try {
      await AuthService.refreshAccessToken(token);
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.RefreshAccessTokenFailed);
      dispatch({
        type: LOGIN_FAIL,
        payload: {
          error: axiosError.request?.responseText || axiosError.message,
        },
      });
    }
  };

// FLAGS: identity, personal-data
// The password sign-in path, and the only one that puts the submitted address into a Redux payload: the
// LOGIN_FAIL branch dispatches `user: { email: userName }` so the login screen can pre-fill it. It also
// swallows the error rather than rethrowing, so the caller learns the outcome only from store state.
export const login =
  (userName: string, password: string, recaptchaToken: string) =>
  async (dispatch: Dispatch) => {
    try {
      const data = await AuthService.login(userName, password, recaptchaToken);
      if (data?.twoFactorVerification === "pending") {
        dispatch({
          type: TWO_FACTOR_PENDING,
          payload: {
            user: data,
          },
        });
      } else {
        dispatchLoginSuccessWithEvent(dispatch, data, "password");
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.LoginFailed);
      dispatch({
        type: LOGIN_FAIL,
        payload: {
          user: {
            email: userName,
          },
          error: {
            message:
              (typeof axiosError.response?.data === "object"
                ? (axiosError.response.data as Record<string, string>)?.title
                : axiosError.response?.data) ||
              axiosError.request?.responseText ||
              axiosError.message,
            status: axiosError.response?.status,
          },
        },
      });
    }
  };

// Two callers: a guest finishing a legacy sign-in, and a signed-in user toggling phone 2FA from
// the profile. Only the first establishes a legacy session, so only there does the Phase Two
// marker go (see dispatchLoginSuccessWithEvent); the toggle may well run on a Phase Two session.
export const twoFactorSuccessAction =
  () => async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const data = await getUserProfile();
      if (!getState().authReducers.isLoggedIn) {
        clearPhaseTwoSession();
      }
      dispatch({
        type: TWO_FACTOR_SUCCESS,
        payload: { user: data },
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.TwoFactorSuccessActionFailed);
      dispatch({
        type: LOGIN_FAIL,
        payload: {
          error: axiosError.request?.responseText || axiosError.message,
        },
      });
      throw error;
    }
  };

export const loginAfterTwoFactorVerification =
  (userEmail: string) => async (dispatch: Dispatch) => {
    try {
      const data = await getUserProfile();
      dispatchLoginSuccessWithEvent(dispatch, data, "password");
    } catch (error) {
      const axiosError = error as AxiosError;
      trackException(AuthInsightEvent.LoginAfterTwoFactorVerificationFailed);
      dispatch({
        type: LOGIN_FAIL,
        payload: {
          user: {
            email: userEmail,
          },
          error: {
            message: axiosError.request?.responseText || axiosError.message,
            status: axiosError.response?.status,
          },
        },
      });
    }
  };

export const resetPasswordEmail =
  (email: string, recaptchaToken: string, redirectUrl?: string) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await AuthService.sendForgotPasswordAccessMail(
        email,
        recaptchaToken,
        redirectUrl,
      );
      dispatch({
        type: FORGOT_PASSWORD_SUCCESS,
        payload: {
          user: { email },
          successMessage: response.message,
          warningMessage: response.isSuccess ? "" : response.message,
        },
      });
      return response;
    } catch (error) {
      trackException(AuthInsightEvent.ForgotPasswordEmailRequestFailed);
      const axiosError = error as AxiosError;
      dispatch({
        type: FORGOT_PASSWORD_FAIL,
        payload: {
          error:
            (typeof axiosError.response?.data === "object"
              ? (axiosError.response.data as Record<string, string>)?.title
              : axiosError.response?.data) ||
            axiosError.request?.responseText ||
            axiosError.message,
        },
      });
      throw error;
    }
  };

export const logout = () => (dispatch: Dispatch) => {
  dispatch({
    type: LOGOUT,
  });
};

export const clearMessages = () => (dispatch: Dispatch) => {
  dispatch({
    type: CLEAR_MESSAGE,
    payload: { successMessage: null, warningMessage: null },
  });
};
